import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { KTSVG } from '../../../_metronic/helpers'
import {
  getFacebookProfiles,
  syncMarketplaceListings,
  FacebookProfile,
} from './core/_requests'

const FacebookProfilesSync: React.FC = () => {
  const navigate = useNavigate()
  const [profiles, setProfiles] = useState<FacebookProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [syncingProfiles, setSyncingProfiles] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadProfiles()
  }, [])

  const loadProfiles = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await getFacebookProfiles()
      setProfiles(response.profiles)
    } catch (err: any) {
      setError(err.response?.data?.error || 'שגיאה בטעינת פרופילים')
    } finally {
      setLoading(false)
    }
  }

  const handleSync = async (profileId: string) => {
    try {
      setSyncingProfiles(prev => new Set(prev).add(profileId))
      setError(null)

      const response = await syncMarketplaceListings(profileId)

      if (response.success) {
        // Reload profiles to get updated counts
        await loadProfiles()
      } else {
        setError(response.error || 'הסנכרון נכשל')
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'שגיאה בסנכרון מודעות')
    } finally {
      setSyncingProfiles(prev => {
        const next = new Set(prev)
        next.delete(profileId)
        return next
      })
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'לא סונכרן'
    return new Date(dateStr).toLocaleString('he-IL')
  }

  // Check if profile is actively running (health check within last 5 minutes)
  const isProfileActive = (lastHealthCheck: string | null | undefined) => {
    if (!lastHealthCheck) return false
    const lastCheck = new Date(lastHealthCheck).getTime()
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000
    return lastCheck > fiveMinutesAgo
  }

  // Format time since last health check
  const formatTimeSinceHealthCheck = (lastHealthCheck: string | null | undefined) => {
    if (!lastHealthCheck) return 'לא פעיל'
    const lastCheck = new Date(lastHealthCheck).getTime()
    const now = Date.now()
    const diffMinutes = Math.floor((now - lastCheck) / (60 * 1000))

    if (diffMinutes < 1) return 'פעיל עכשיו'
    if (diffMinutes < 60) return `לפני ${diffMinutes} דקות`
    const diffHours = Math.floor(diffMinutes / 60)
    if (diffHours < 24) return `לפני ${diffHours} שעות`
    const diffDays = Math.floor(diffHours / 24)
    return `לפני ${diffDays} ימים`
  }

  // Facebook access token for profile pictures
  const FB_ACCESS_TOKEN = process.env.REACT_APP_FB_ACCESS_TOKEN

  // Get profile picture URL - use Facebook's Graph API or stored URL
  const getProfilePicture = (profile: FacebookProfile) => {
    if (profile.profile_picture) return profile.profile_picture
    // Fallback to Facebook Graph API with access token
    return `https://graph.facebook.com/${profile.facebook_id}/picture?type=large&access_token=${FB_ACCESS_TOKEN}`
  }

  if (loading) {
    return (
      <div className="card">
        <div className="card-body d-flex justify-content-center align-items-center py-10">
          <span className="spinner-border spinner-border-sm me-2"></span>
          טוען פרופילים...
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="card-header border-0 pt-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bold fs-3 mb-1">פרופילים מחוברים</span>
          <span className="text-muted mt-1 fw-semibold fs-7">
            פרופילים מחוברים לסנכרון מרקטפלייס
          </span>
        </h3>
        <div className="card-toolbar">
          <button
            className="btn btn-sm btn-light-primary"
            onClick={loadProfiles}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr029.svg' className='svg-icon-3 me-1' />
            רענן
          </button>
        </div>
      </div>

      <div className="card-body py-3">
        {error && (
          <div className="alert alert-danger d-flex align-items-center mb-4">
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2 text-danger me-3' />
            {error}
          </div>
        )}

        {profiles.length === 0 ? (
          <div className="text-center py-10 text-muted">
            <KTSVG path='/media/icons/duotune/communication/com006.svg' className='svg-icon-3x mb-3' />
            <p>לא נמצאו פרופילים מחוברים</p>
            <p className="fs-7">ודא שהפרופילים שלך מחוברים דרך סקריפט האוטומציה</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
              <thead>
                <tr className="fw-bold text-muted">
                  <th className="min-w-150px">פרופיל</th>
                  <th className="min-w-100px">סטטוס</th>
                  <th className="min-w-120px">מודעות</th>
                  <th className="min-w-150px">סנכרון אחרון</th>
                  <th className="min-w-100px text-end">פעולות</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map(profile => (
                  <React.Fragment key={profile.id}>
                    <tr>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="symbol symbol-45px me-5">
                            <img
                              src={getProfilePicture(profile)}
                              alt={profile.name}
                              className="symbol-label"
                              style={{ objectFit: 'cover', borderRadius: '50%' }}
                              onError={(e) => {
                                // Fallback to default icon if image fails to load
                                (e.target as HTMLImageElement).style.display = 'none'
                                const parent = (e.target as HTMLImageElement).parentElement
                                if (parent) {
                                  parent.innerHTML = '<div class="symbol-label bg-light-primary"><span class="svg-icon svg-icon-2 text-primary"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg></span></div>'
                                }
                              }}
                            />
                          </div>
                          <div className="d-flex justify-content-start flex-column">
                            <span className="text-dark fw-bold text-hover-primary fs-6">
                              {profile.name}
                            </span>
                            <span className="text-muted fw-semibold d-block fs-7">
                              {profile.facebook_id}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          {profile.is_logged_in ? (
                            <span className="badge badge-light-success mb-1">מחובר</span>
                          ) : (
                            <span className="badge badge-light-danger mb-1">מנותק</span>
                          )}
                          {profile.is_logged_in && (
                            <div className="d-flex align-items-center">
                              <span
                                className={`bullet bullet-dot me-2 ${
                                  isProfileActive(profile.last_health_check) ? 'bg-success' : 'bg-warning'
                                }`}
                                style={{ width: '8px', height: '8px' }}
                              />
                              <span className="text-muted fs-8">
                                {formatTimeSinceHealthCheck(profile.last_health_check)}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-light-info fs-7">
                          {profile.synced_listings_count} מודעות
                        </span>
                      </td>
                      <td>
                        <span className="text-muted fs-7">
                          {formatDate(profile.last_synced_at)}
                        </span>
                      </td>
                      <td className="text-end">
                        <button
                          className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                          onClick={() => navigate(`/marketplace/${profile.id}`)}
                          title="הצג מודעות"
                        >
                          <KTSVG
                            path='/media/icons/duotune/arrows/arr072.svg'
                            className='svg-icon-3'
                          />
                        </button>
                        <button
                          className="btn btn-sm btn-light-primary"
                          onClick={() => handleSync(profile.id)}
                          disabled={syncingProfiles.has(profile.id) || !profile.is_logged_in}
                          title={!profile.is_logged_in ? 'הפרופיל חייב להיות מחובר לסנכרון' : 'סנכרן מודעות מפייסבוק'}
                        >
                          {syncingProfiles.has(profile.id) ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-1"></span>
                              מסנכרן...
                            </>
                          ) : (
                            <>
                              <KTSVG path='/media/icons/duotune/arrows/arr029.svg' className='svg-icon-3 me-1' />
                              סנכרן
                            </>
                          )}
                        </button>
                      </td>
                    </tr>

                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default FacebookProfilesSync
